"use client";

import { useLocale, useTranslations } from "next-intl";
import type { Board, ObjectKind } from "@engine/types";
import { objectName } from "@/themes/registry";
import type { ThemeDefinition } from "@/themes/types";
import { FurnitureIcon } from "./FurnitureIcon";

/**
 * What is on this board, named.
 *
 * A drawn shape is a guess until someone tells you what it is, and the clues talk about "the
 * cauldron" as if you already knew which one that was. Only the kinds actually used on this board
 * are listed, so the legend stays short and is about the case in front of you. Names come from the
 * case's theme — the same names the clues use.
 *
 * Whether a piece can be stood on is the other thing the shapes encode, so it is spelled out here
 * too rather than left to be inferred from how solid a drawing looks.
 */
export function BoardLegend({ board, theme }: { board: Board; theme: ThemeDefinition }) {
  const t = useTranslations("objects");
  const locale = useLocale();

  const kinds = new Map<ObjectKind, boolean>();
  for (const object of board.objects) {
    kinds.set(object.kind as ObjectKind, object.occupiable);
  }
  if (kinds.size === 0) return null;

  const nameOf = (kind: ObjectKind): string => objectName(theme, locale, kind);
  const sorted = [...kinds].sort(([a], [b]) => nameOf(a).localeCompare(nameOf(b)));

  return (
    <section data-testid="board-legend" style={{ marginTop: "1rem" }}>
      <h2 style={{ fontSize: "0.95rem", margin: "0 0 0.5rem", color: "var(--muted)" }}>
        {t("legend")}
      </h2>
      <ul
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.4rem 1.1rem",
          listStyle: "none",
          margin: 0,
          padding: 0,
          fontSize: "0.88rem",
        }}
      >
        {sorted.map(([kind, occupiable]) => (
          <li
            key={kind}
            data-testid={`legend-${kind}`}
            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
          >
            <svg width={26} height={26} viewBox="0 0 26 26" aria-hidden="true">
              <FurnitureIcon
                kind={kind}
                label={nameOf(kind)}
                x={0}
                y={0}
                size={26}
                occupiable={occupiable}
                theme={theme}
              />
            </svg>
            <span>{nameOf(kind)}</span>
            <span style={{ color: "var(--muted)" }}>
              — {occupiable ? t("canStand") : t("cannotStand")}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
